import { FlatList, View, Text } from "react-native";
import { AssessmentSection } from "../organisms/AssessmentSection";
import { useAssessmentFormContext } from "../../assessments/providers/AssessmentFormProvider";
import { Button, ButtonText } from "ui/gluestack/button";
import { useRouter } from "expo-router";
import { ComponentRef } from "react";

type Props = {
  cancelPath?: string;
  sectionRefs: React.RefObject<{ [key: number]: ComponentRef<typeof View> }>;
};

export default function AssessmentFormTemplate({
  sectionRefs,
  cancelPath,
}: Props) {
  const {
    sections,
    currentPageIndex,
    totalPages,
    setCurrentPageIndex,
    isSubmitting,
    assessmentModel,
    handleSubmit,
    submitForm,
    saveAsDraftForm,
  } = useAssessmentFormContext();

  const router = useRouter();
  const cancel = () => {
    if (cancelPath) router.navigate(cancelPath);
  };

  const submit = handleSubmit(submitForm);
  const saveAsDraft = handleSubmit(saveAsDraftForm);
  const next = handleSubmit(() => setCurrentPageIndex(currentPageIndex + 1));
  const previous = handleSubmit(() =>
    setCurrentPageIndex(currentPageIndex - 1),
  );

  return (
    <FlatList
      data={sections}
      renderItem={({ item }) => (
        <View
          ref={(el) => {
            if (sectionRefs.current && el) sectionRefs.current[item.id] = el;
          }}
        >
          <AssessmentSection section={item} />
        </View>
      )}
      keyExtractor={({ id }) => `section-${id}`}
      contentContainerClassName="gap-8"
      ListFooterComponent={() => (
        <View className="flex-row gap-4 justify-between items-center">
          {totalPages > 1 ? (
            <Text className="text-typography-600 text-sm">
              Page {currentPageIndex + 1} of {totalPages}
            </Text>
          ) : null}
          <View className="flex-row flex-1 gap-4 justify-end">
            {cancelPath ? (
              <Button
                disabled={isSubmitting}
                onPress={cancel}
                action="primary"
                variant="outline"
              >
                <ButtonText>Cancel</ButtonText>
              </Button>
            ) : null}

            {totalPages > 1 ? (
              <Button
                disabled={currentPageIndex === 0 || isSubmitting}
                onPress={previous}
                variant="outline"
              >
                <ButtonText>Previous</ButtonText>
              </Button>
            ) : null}

            {assessmentModel.can_have_draft_status ? (
              <Button
                disabled={isSubmitting}
                onPress={saveAsDraft}
                variant="outline"
                action="primary"
              >
                <ButtonText>Save as draft</ButtonText>
              </Button>
            ) : null}

            {totalPages > 1 && currentPageIndex < totalPages - 1 ? (
              <Button disabled={isSubmitting} onPress={next} action="primary">
                <ButtonText>Next</ButtonText>
              </Button>
            ) : null}

            {totalPages === 1 || currentPageIndex === totalPages - 1 ? (
              <Button disabled={isSubmitting} onPress={submit} action="primary">
                <ButtonText>Submit</ButtonText>
              </Button>
            ) : null}
          </View>
        </View>
      )}
    ></FlatList>
  );
}
